// src/pages/admin/Downloads.jsx
import { useMemo, useState } from "react";
import { Card, Button } from "@common";
import { toast } from "react-hot-toast";
import { useOverviewForecastTotal, useOverviewPerformance, useOverviewPatients } from "@api/hooks";
import { torontoToday } from "@utils";
import * as XLSX from "xlsx";

export default function Downloads() {
  // today as YYYY-MM-DD (for <input type="date">)
  const today = useMemo(() => torontoToday(), []);

  const [range, setRange] = useState({
    start: today,
    end: today,
  });

  const startDate = range.start;
  const endDate = range.end;

  // Determine if we're using a date range or single date
  const isDateRange = startDate !== endDate;
  const dateParams = isDateRange ? { date_from: startDate, date_to: endDate } : { date: endDate };

  // ------- HOOKS: same data sources as your dashboard -------

  // Forecasts (overview)
  const {
    data: forecastData,
    isLoading: forecastLoading,
    error: forecastError,
  } = useOverviewForecastTotal(dateParams, { enablePolling: true });

  // Performance (overview)
  const {
    data: performanceData,
    isLoading: performanceLoading,
    error: performanceError,
  } = useOverviewPerformance(
    {
      ...dateParams,
      limit: 10000,
      offset: 0,
    },
    { enablePolling: true },
  );

  // Patients (overview)
  const {
    data: patientsData,
    isLoading: patientsLoading,
    error: patientsError,
  } = useOverviewPatients(
    {
      ...dateParams,
      limit: 10000,
      offset: 0,
    },
    { enablePolling: true },
  );

  const rows = [
    { id: "hist-perf", label: "Historical Performance Report (Before July)" },
    { id: "forecast", label: "Forecast Report" },
    { id: "new-patient", label: "New Patient Report" },
    { id: "new-performance", label: "New Performance Report" },
  ];

  async function handleClick(id) {
    if (!range.start || !range.end) {
      toast.error("Please select a valid date range first.");
      return;
    }

    try {
      switch (id) {
        case "forecast":
          await downloadForecastReport();
          break;
        case "new-patient":
          await downloadNewPatientReport();
          break;
        case "new-performance":
          await downloadNewPerformanceReport();
          break;
        case "hist-perf":
          await downloadHistoricalPerformanceReport();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to download report.");
    }
  }

  // ----------------- FORECAST REPORT -----------------

  async function downloadForecastReport() {
    if (forecastLoading) {
      toast("Forecast data is still loading, please wait...");
      return;
    }
    if (forecastError) {
      toast.error("Could not load forecast data.");
      return;
    }

    const results = forecastData?.results || [];
    if (!results.length) {
      toast("No forecast data for this date range.");
      return;
    }

    const dateLabel = isDateRange ? `${startDate}_to_${endDate}` : endDate;

    // Build rows: Practice | Date | MTD Prod | Forecast | Total | Period
    const rows = results.map((clinic) => {
      const mtd = clinic.mtd_production;
      const forecast = clinic.forecast;

      const total = typeof mtd === "number" && typeof forecast === "number" ? mtd + forecast : null;

      // Date column: use clinic.date (the specific date for this forecast)
      const clinicDate = clinic.date ? new Date(clinic.date + "T00:00:00") : null;
      const displayDate = clinicDate ? clinicDate.toLocaleDateString("en-US") : "";

      // Period: use mtd_period.from (month/year only)
      const periodFrom = clinic.mtd_period?.from
        ? new Date(clinic.mtd_period.from + "T00:00:00")
        : null;
      const periodDisplay = periodFrom
        ? `${periodFrom.getMonth() + 1}/${periodFrom.getFullYear()}`
        : "";

      return {
        Practice: clinic.clinic,
        Date: displayDate,
        "MTD Prod": mtd ?? "",
        Forecast: forecast ?? "",
        Total: total ?? "",
        Period: periodDisplay,
      };
    });

    // Create Excel workbook + sheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Forecast Report");

    const filename = `forecasts_${dateLabel}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success("Forecast report downloaded.");
  }

  // ----------------- NEW PATIENT REPORT -----------------

  async function downloadNewPatientReport() {
    if (patientsLoading) {
      toast("Patients data is still loading, please wait...");
      return;
    }
    if (patientsError) {
      toast.error("Could not load patients data.");
      return;
    }

    const overviewResults = patientsData?.results || [];
    if (!overviewResults.length) {
      toast(`No patients data available for selected date range.`);
      return;
    }

    const rows = [];

    overviewResults.forEach((clinicData) => {
      const clinic = clinicData.clinic;
      const patients = clinicData.patients || [];

      patients.forEach((p) => {
        // use the patient’s own date for DATE column
        const isoDate = p.date || endDate;
        const d = isoDate ? new Date(isoDate) : null;
        const displayDate = d ? d.toLocaleDateString("en-US") : "";

        // PERIOD = month/year
        const periodDisplay = d ? `${d.getMonth() + 1}/${d.getFullYear()}` : "";

        const leadStr = p.lead || "";
        const parts = leadStr.split(" - ");
        const leadCategory = (parts[0] || "").trim();
        const leadDescription = (parts[1] || "").trim();

        rows.push({
          DATE: displayDate,
          PRACTICE: clinic,
          PATIENT: p.name || "",
          "LEAD CATEGORY": leadCategory,
          DESCRIPTION: leadDescription,
          PERIOD: periodDisplay,
        });
      });
    });

    if (!rows.length) {
      toast(`No patients data available for selected date range.`);
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "New Patients");

    const dateLabel = isDateRange ? `${startDate}_to_${endDate}` : endDate;
    const filename = `new_patients_${dateLabel}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success("New Patient report downloaded.");
  }

  // ----------------- NEW PERFORMANCE REPORT -----------------

  async function downloadNewPerformanceReport() {
    if (performanceLoading) {
      toast("Performance data is still loading, please wait...");
      return;
    }
    if (performanceError) {
      toast.error("Could not load performance data.");
      return;
    }

    const overviewResults = performanceData?.results || [];
    if (!overviewResults.length) {
      toast(`No performance data available for selected date range.`);
      return;
    }

    // We want to match:
    // Practice | Date | Category | Practitioner DDS/HYG | Hours Worked |
    // Lab Costs | Invisalign Costs | Material Costs | Production |
    // Credit | Cash | Electronic | Other | Collection | Period

    const rows = [];

    overviewResults.forEach((clinicData) => {
      const clinic = clinicData.clinic;

      // Date: use clinic.date (the specific date for this performance data)
      const clinicDate = clinicData.date ? new Date(clinicData.date + "T00:00:00") : null;
      const displayDate = clinicDate ? clinicDate.toLocaleDateString("en-US") : "";

      // Period: month/year from the date
      const periodDisplay = clinicDate
        ? `${clinicDate.getMonth() + 1}/${clinicDate.getFullYear()}`
        : "";

      // Practitioner rows
      const practitioners = clinicData.practitionerBreakdown || [];
      practitioners.forEach((pr) => {
        const practitionerName = pr.practitioner_name || pr.practitioner || "";
        const practitionerType = pr.practitioner_type || "";
        const fullName = practitionerType
          ? `${practitionerName} ${practitionerType}`.trim()
          : practitionerName;

        rows.push({
          Practice: clinic,
          Date: displayDate,
          Category: pr.category || "performance",
          "Practitioner DDS/HYG": fullName,
          Production: pr.practitioner_production ?? "",
          Collection: pr.practitioner_collection ?? "",
          Cash: "",
          Debit: "",
          Mastercard: "",
          Visa: "",
          Amex: "",
          Checks: "",
          "Insurance check": "",
          "Personal EFTs": "",
          "Insurance EFTs": "",
          Other: "",
          "Lab Costs": pr.lab_costs ?? "",
          "Material Costs": pr.material_costs ?? "",
          "Invisalign Costs": pr.invisalign_costs ?? "",
          "Anesthesia Fee": pr.anesthesia_fee ?? "",
          "Anesthesiologist Fee": pr.anesthesiologist_fee ?? "",
          "Facility Fee": pr.facility_fee ?? "",
          Botox: pr.botox ?? "",
          "Production Adjustment": pr.production_adjustment ?? "",
          "Collection Adjustment": pr.collection_adjustment ?? "",
          "Hours Worked": pr.hours_worked ?? "",
          Period: periodDisplay,
        });
      });

      // Collection summary row from costAndCollection (Category: "collection")
      const cc = clinicData.costAndCollection || null;
      if (cc) {
        rows.push({
          Practice: clinic,
          Date: displayDate,
          Category: "collection",
          "Practitioner DDS/HYG": "",
          Production: clinicData.performance?.production ?? "",
          Collection: cc.collection_total ?? "",
          Cash: cc.collection_cash ?? "",
          Debit: cc.collection_debit ?? "",
          Mastercard: cc.collection_mastercard ?? "",
          Visa: cc.collection_visa ?? "",
          Amex: cc.collection_amex ?? "",
          Checks: cc.collection_checks ?? "",
          "Insurance check": cc.collection_insurance_check ?? "",
          "Personal EFTs": cc.collection_personal_eft ?? "",
          "Insurance EFTs": cc.collection_insurance_eft ?? "",
          Other: cc.collection_other ?? "",
          "Lab Costs": cc.lab_costs ?? "",
          "Material Costs": cc.material_costs ?? "",
          "Invisalign Costs": cc.invisalign_costs ?? "",
          "Anesthesia Fee": "",
          "Facility Fee": "",
          Botox: "",
          "Production Adjustment": "",
          "Collection Adjustment": "",
          "Hours Worked": cc.hours_worked ?? "",
          Period: periodDisplay,
        });
      }
    });

    if (!rows.length) {
      toast(`No performance data available for selected date range.`);
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Performance");

    const dateLabel = isDateRange ? `${startDate}_to_${endDate}` : endDate;
    const filename = `performance_${dateLabel}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success("New Performance report downloaded.");
  }

  // ----------------- HISTORICAL PERFORMANCE REPORT -----------------

  async function downloadHistoricalPerformanceReport() {
    if (performanceLoading) {
      toast("Performance data is still loading, please wait...");
      return;
    }
    if (performanceError) {
      toast.error("Could not load performance data.");
      return;
    }

    const overviewResults = performanceData?.results || [];
    if (!overviewResults.length) {
      toast("No performance data available for selected date range.");
      return;
    }

    const rows = [];

    overviewResults.forEach((clinicData) => {
      const clinic = clinicData.clinic;

      const clinicDate = clinicData.date ? new Date(clinicData.date + "T00:00:00") : null;
      const displayDate = clinicDate ? clinicDate.toLocaleDateString("en-US") : "";

      const periodDisplay = clinicDate
        ? `${clinicDate.getMonth() + 1}/${clinicDate.getFullYear()}`
        : "";

      const practitioners = clinicData.practitionerBreakdown || [];
      practitioners.forEach((pr) => {
        const practitionerName = pr.practitioner_name || pr.practitioner || "";
        const practitionerType = pr.practitioner_type || "";
        const fullName = practitionerType
          ? `${practitionerName} ${practitionerType}`.trim()
          : practitionerName;

        rows.push({
          Practice: clinic,
          Date: displayDate,
          Category: pr.category || "performance",
          "Practitioner DDS/HYG": fullName,
          Production: pr.practitioner_production ?? "",
          Collection: pr.practitioner_collection ?? "",
          "Lab Costs": pr.lab_costs ?? "",
          "Material Costs": pr.material_costs ?? "",
          "Invisalign Costs": pr.invisalign_costs ?? "",
          "Anesthesia Fee": pr.anesthesia_fee ?? "",
          "Anesthesiologist Fee": pr.anesthesiologist_fee ?? "",
          "Facility Fee": pr.facility_fee ?? "",
          Botox: pr.botox ?? "",
          "Production Adjustment": pr.production_adjustment ?? "",
          "Collection Adjustment": pr.collection_adjustment ?? "",
          "Hours Worked": pr.hours_worked ?? "",
          Period: periodDisplay,
        });
      });

      // Collection summary row
      const cc = clinicData.costAndCollection || null;
      if (cc) {
        rows.push({
          Practice: clinic,
          Date: displayDate,
          Category: "collection",
          "Practitioner DDS/HYG": "",
          Production: clinicData.performance?.production ?? "",
          Collection: cc.collection_total ?? "",
          "Lab Costs": cc.lab_costs ?? "",
          "Material Costs": cc.material_costs ?? "",
          "Invisalign Costs": cc.invisalign_costs ?? "",
          "Anesthesia Fee": "",
          "Facility Fee": "",
          Botox: "",
          "Production Adjustment": "",
          "Collection Adjustment": "",
          "Hours Worked": cc.hours_worked ?? "",
          Period: periodDisplay,
        });
      }
    });

    if (!rows.length) {
      toast("No performance data available for selected date range.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Historical Performance");

    const dateLabel = isDateRange ? `${startDate}_to_${endDate}` : endDate;
    const filename = `historical_performance_${dateLabel}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success("Historical Performance report downloaded.");
  }

  return (
    <Card title="Download Reports" className="mt-2">
      {/* Date range selector */}
      <div className="flex flex-col items-center gap-3 mb-6">
        <p className="text-palette-gray-heading">
          Please select a date range to download reports for:
        </p>

        <div className="flex items-center gap-2 bg-white border border-palette-gray-divider rounded-xl px-4 py-2 shadow-sm">
          <input
            type="date"
            className="border-none outline-none text-palette-gray-text rounded-lg px-1 py-1"
            value={range.start}
            max={range.end}
            onChange={(e) => setRange((prev) => ({ ...prev, start: e.target.value }))}
          />
          <span className="text-palette-gray-secondary">–</span>
          <input
            type="date"
            className="border-none outline-none text-palette-gray-text rounded-lg px-1 py-1"
            value={range.end}
            min={range.start}
            onChange={(e) => setRange((prev) => ({ ...prev, end: e.target.value }))}
          />
        </div>
      </div>

      {/* List of report types */}
      <div className="divide-y divide-palette-indigo-soft rounded-2xl overflow-hidden bg-palette-indigo-soft/20">
        {rows.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between bg-white mx-4 my-3 rounded-xl px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
          >
            <div className="text-[15px] font-semibold text-palette-gray-primary">{r.label}</div>

            <Button
              onClick={() => handleClick(r.id)}
              className="rounded-full bg-palette-indigo-badge text-white px-5 py-2 font-semibold hover:bg-palette-indigo-heading"
            >
              Download
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
